import React, { useEffect, useMemo, useState } from 'react';
import { Select, Space } from 'antd';
import { useQuery } from '@tanstack/react-query';
import leadService from '@/services/lead.service';
import opportunityService from '@/services/opportunity.service';
import customerService from '@/services/customer.service';

export type CrmRecordKind = 'customer' | 'lead' | 'opportunity';

export interface CrmRecordLink {
  kind: CrmRecordKind;
  id: string;
}

interface Props {
  value?: CrmRecordLink | null;
  onChange?: (value: CrmRecordLink | null) => void;
  kinds?: CrmRecordKind[];
  /** Label of the preselected record, shown until it appears in the search results. */
  valueLabel?: string;
}

const KIND_LABEL: Record<CrmRecordKind, string> = {
  customer: 'Customer',
  lead: 'Lead',
  opportunity: 'Opportunity',
};

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Pick the customer, lead or opportunity an activity is logged against. */
const CrmRecordPicker: React.FC<Props> = ({ value, onChange, kinds = ['customer', 'lead', 'opportunity'], valueLabel }) => {
  const [kind, setKind] = useState<CrmRecordKind>(value?.kind ?? kinds[0]);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);

  useEffect(() => {
    if (value?.kind && value.kind !== kind) setKind(value.kind);
  }, [value?.kind, kind]);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['crm-record-picker', kind, debounced],
    queryFn: async (): Promise<Array<{ value: string; label: string }>> => {
      if (kind === 'lead') {
        const rows = await leadService.getAll({ search: debounced || undefined });
        return rows.slice(0, 50).map((l) => ({ value: l.id, label: `${l.lead_number} — ${l.customer_name}` }));
      }
      if (kind === 'opportunity') {
        const rows = await opportunityService.getAll({ search: debounced || undefined });
        return rows
          .slice(0, 50)
          .map((o) => ({ value: o.id, label: `${o.opportunity_number} — ${o.opportunity_name} (${o.customer_name})` }));
      }
      const res = await customerService.getAllCustomers({ search: debounced || undefined, limit: 50, is_active: true });
      const body = res as unknown;
      const rows: Array<{ id: string; customer_code: string; customer_name: string }> = Array.isArray(
        (body as { data?: unknown })?.data
      )
        ? (body as { data: Array<{ id: string; customer_code: string; customer_name: string }> }).data
        : Array.isArray(body)
          ? (body as Array<{ id: string; customer_code: string; customer_name: string }>)
          : [];
      return rows.map((c) => ({
        value: c.id,
        label: `${c.customer_code} — ${c.customer_name}`,
      }));
    },
  });

  const selectOptions = useMemo(() => {
    if (!value?.id || value.kind !== kind || !valueLabel || options.some((o) => o.value === value.id)) return options;
    return [{ value: value.id, label: valueLabel }, ...options];
  }, [options, value, kind, valueLabel]);

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        style={{ width: 140 }}
        value={kind}
        options={kinds.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
        onChange={(k: CrmRecordKind) => {
          setKind(k);
          setSearch('');
          onChange?.(null);
        }}
      />
      <Select
        showSearch
        allowClear
        style={{ flex: 1, minWidth: 220 }}
        placeholder={`Search ${KIND_LABEL[kind].toLowerCase()}…`}
        filterOption={false}
        loading={isFetching}
        onSearch={setSearch}
        value={value?.kind === kind ? value.id : undefined}
        options={selectOptions}
        onChange={(id?: string) => onChange?.(id ? { kind, id } : null)}
        notFoundContent={isFetching ? 'Searching…' : 'No matches'}
      />
    </Space.Compact>
  );
};

export default CrmRecordPicker;
